
import { Component, Mixins, Inject, Provide } from "vue-property-decorator";

// Enums/Data
import { DataModules } from "@/store/modules/data";
import { BrandConfigKeys, QUERY_PARAMS } from "@/data/constants";

// Types
import {
  IBasketProviderData,
  IBasketProviderMethods
} from "@/models/providers/baskets";
import {
  ICheckoutProviderData,
  ICheckoutProviderMethods
} from "@/models/providers/checkout";
import { IShopProviderMethods } from "@/models/providers/basket/shop";
import { IInvoice } from "@/models/invoices";

import PaymentProvider from "@/components/app/global/payments/selectPaymentMethod/provider.vue";
import Context from "@/mixins/context";
import _ from "@/boot/lodash";
import { GatewayStoreType } from "@/data/enums/gateway";
import { Methods } from "@/models/methods";
import { Location as VueLocation } from "vue-router";
import { PaymentMethodType } from "@/models/selectPaymentMethod";

@Component
export default class CheckoutProvider extends Mixins(PaymentProvider, Context) {
  // Inject

  @Inject({ from: "basketProviderData" })
  public readonly basketProviderData!: () => IBasketProviderData;

  @Inject({ from: "basketProviderMethods" })
  public readonly $basketMethods!: IBasketProviderMethods;

  @Inject({ from: "shopProviderMethods" })
  public readonly $shopMethods!: IShopProviderMethods;

  // Data

  public guestEmail = "";
  public invoice = {} as IInvoice;
  public isProcessing = false;

  // Computed

  public get $basketData() {
    return this.basketProviderData();
  }

  // REQUIRED: Overrides payment provider mixin
  public get totalAmountToPay() {
    return this.$basketData.totalAmountToPay;
  }

  public get hasPartialPayment() {
    if (this.isAdmin) return true;
    return this.$basketData.brandData.config[
      BrandConfigKeys.PARTIAL_PAYMENTS_ENABLED
    ];
  }

  public get hasPayLater() {
    if (this.isAdmin) return true;
    return this.$basketData.brandData.config[BrandConfigKeys.PAY_LATER_ENABLED];
  }

  public get additionalManualPaymentData() {
    return {
      invoice_id: this.invoice.id
    };
  }

  public get additionalPaymentData() {
    const origin = window.location.origin;

    const returnRoute = this.$router.resolve(this.externalAuthReturnLocation)
      .route;

    const successLocation = this.$_.merge({}, returnRoute, {
      query: {
        [QUERY_PARAMS.PAYMENT_SUCCESS]: "true",
        [QUERY_PARAMS.ATTEMPT]: this.attemptKey
      }
    });

    const failLocation = this.$_.merge({}, returnRoute, {
      query: {
        [QUERY_PARAMS.PAYMENT_SUCCESS]: "false",
        [QUERY_PARAMS.ATTEMPT]: this.attemptKey
      }
    });

    const returnUrlEncodedSuccess = encodeURIComponent(
      `${origin}${successLocation.fullPath}`
    );

    const returnUrlEncodedFail = encodeURIComponent(
      `${origin}${failLocation.fullPath}`
    );

    return {
      client_id: this.$basketData.clientId,
      account_id: this.$basketData.accountId,
      invoice_id: this.invoice?.id || this.$basketData.basketId || "",
      return_url: `?${QUERY_PARAMS.SUCCESS}=${returnUrlEncodedSuccess}&${QUERY_PARAMS.FAILED}=${returnUrlEncodedFail}`,
      cancel_url: `${origin}${returnRoute.fullPath}`
    };
  }

  public get attemptKey() {
    return btoa(
      JSON.stringify({
        invoiceId: this.$basketData.basketId,
        timestamp: this.$moment()
          .toDate()
          .getTime()
      })
    );
  }

  public get excludedGatewayStoreTypes() {
    return this.$basketData.clientIsGuest ? [GatewayStoreType.ALWAYS] : [];
  }

  public get canCheckout() {
    const { walletAmount, amount, type, data } = this.paymentMethod ?? {};

    // BLOCK
    if (this.isGuest || !this.$basketData.client) return false;
    // BLOCK IF address is required and address_id is not set
    if (
      this.$basketData.isAddressRequiredAtCheckout &&
      !this.$basketData.basket?.address_id
    )
      return false;
    // ALLOW = IF nothing to pay
    if (!this.totalAmountToPay) return true;
    // ALLOW = IF wallet balance equals amount to pay
    if (_.isNumber(amount) && walletAmount === amount) return true;
    // ALLOW = IF opting to pay later
    if (type === PaymentMethodType.PAY_LATER) return true;
    // BLOCK = IF no selected payment type
    if (!type || _.isEmpty(data)) return false;
    // ALLOW
    return true;
  }

  // Override
  public get externalAuthReturnLocation(): VueLocation {
    const routes = this.isExternalStore
      ? ["adminOrderCheckout", "orderCheckoutStep"] // External store i.e GoCardless
      : ["adminOrderComplete", "orderCompleteStep"]; // 3D Secure cards, i.e SCA

    return this.buildResolver({
      returnLocation: {
        name: this.isAdmin ? routes[0] : routes[1],
        params: {
          basketId: `${this.$basketData.basketId}`
        },
        query: {
          [QUERY_PARAMS.ORDER_ID]: `${this.$basketData.basketId}`
        }
      },
      paymentMethodType: this.paymentMethodType,
      initPay: this.isExternalStore
        ? { invoiceId: this.$basketData.basketId }
        : undefined
    }).location;
  }

  // Methods

  public updateGuestEmail() {
    return this.$store.dispatch("api/call", {
      method: Methods.PUT,
      path: `api/clients/${this.$basketData.client.id}`,
      requestConfig: { data: { email: this.guestEmail } }
    });
  }

  public async doCheckout() {
    if (this.isExternalStore) return;
    try {
      this.isProcessing = true;
      this.$basketMethods.set("isProcessing", true);
      // If client is guest, update their email
      if (this.$basketData.clientIsGuest && this.guestEmail)
        await this.updateGuestEmail();

      // Set attempt key BEFORE converting order (incase 0.00 total === paid)
      const attemptKey = this.attemptKey;
      // Prepare payment data
      await this.preparePaymentData(this.$basketData.clientId);

      // Convert basket to order invoice
      try {
        this.invoice = (await this.$store.dispatch(
          `data/${DataModules.BASKETS}/convertToInvoice`,
          {
            id: this.$basketData.basketId,
            isClient: !this.isAdmin,
            data: this.paymentData
          }
        )) as IInvoice;
      } catch (error) {
        if (this.$store.getters["api/is409"](error)) {
          try {
            await this.handle409ExistingInvoice(error);
            return;
          } catch (error) {
            // silent catch
          }
        }
      }

      // Check whether external auth/verification is required
      if (this.externalAuth)
        await this.redirectToExternalAuth(this.externalAuth);

      this.$basketMethods.set("isComplete", true);

      try {
        await this.executePayment(attemptKey);
        if (!(this.paymentMethod?.data?.external || false)) {
          this.$shopMethods.goToComplete({
            ...(this.isAdmin
              ? {}
              : { [QUERY_PARAMS.ORDER_ID]: this.invoice.id }),
            [QUERY_PARAMS.ATTEMPT]: attemptKey
          });
        }
      } catch (error) {
        this.$shopMethods.goToComplete({
          ...(this.isAdmin ? {} : { [QUERY_PARAMS.ORDER_ID]: this.invoice.id }),
          [QUERY_PARAMS.PAYMENT_SUCCESS]: false
        });
      }
    } finally {
      this.isProcessing = false;
      this.$basketMethods.set("isProcessing", true);
    }
  }

  public async handle409ExistingInvoice(error) {
    // Checking if really the invoice exists
    const invoice = await this.$store.dispatch(
      `data/${DataModules.INVOICES}/get`,
      {
        id: this.$basketData.basketId,
        storeData: false,
        returnData: true,
        vm: this
      }
    );

    // If invoice already exists and basket is finished go to complete page
    if (!_.isEmpty(invoice?.current_data)) {
      this.$shopMethods.goToComplete({
        ...(this.isAdmin
          ? {}
          : { [QUERY_PARAMS.ORDER_ID]: this.$basketData.basketId })
      });
    } else {
      this.$store.dispatch("api/handleError", error);
      await this.$basketMethods.refreshBasketData(true);
    }
  }

  // Provide
  @Provide("checkoutProviderData")
  public providerData(): ICheckoutProviderData {
    return {
      canCheckout: this.canCheckout,
      excludedGatewayStoreTypes: this.excludedGatewayStoreTypes,
      externalAuthReturnLocation: this.externalAuthReturnLocation,
      guestEmail: this.guestEmail,
      hasPartialPayment: this.hasPartialPayment,
      hasPayLater: this.hasPayLater,
      isProcessing: this.isProcessing,
      paymentDetailIdFromExternalAuth: this.paymentDetailIdFromExternalAuth,
      paymentMethod: this.paymentMethod
    };
  }

  @Provide("checkoutProviderMethods")
  public get providerMethods(): ICheckoutProviderMethods {
    return {
      doCheckout: this.doCheckout,
      handle409ExistingInvoice: this.handle409ExistingInvoice,
      set: (...args) => this.$set(this, ...args)
    };
  }

  // Life-cycle Methods

  public created() {
    this.checkExternalAuthQueryParams();
  }

  public render() {
    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $checkoutData: this.providerData(),
        $checkoutMethods: this.providerMethods
      })
    );
  }
}
